'use client';

import Link from 'next/link';

import { zodResolver } from '@hookform/resolvers/zod';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { AlertDescription } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import { PasswordSignInSchema } from '../schemas/password-sign-in.schema';
import InputAuth from './input';
import { AuthButton } from './auth-button';

export function PasswordSignInForm({
  onSubmit,
  loading,
  error,
}: {
  onSubmit: (params: z.infer<typeof PasswordSignInSchema>) => unknown;
  loading: boolean;
  error: Error | null | undefined | string;
}) {
  const { t } = useTranslation('auth');

  const form = useForm<z.infer<typeof PasswordSignInSchema>>({
    resolver: zodResolver(PasswordSignInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const DefaultError = <Trans i18nKey="auth:errors.default" />;
  const errorCode = error instanceof Error ? error.message : error;

  return (
    <Form {...form}>
      <form
        className={'w-full space-y-4'}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name={'email'}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAuth
                  data-test={'email-input'}
                  required
                  type="email"
                  placeholder={t('common:emailAddress')}
                  error={!!form.formState.errors.email || !!error}
                  {...field}
                />
              </FormControl>

              {/* <FormMessage /> */}
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'password'}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAuth
                  required
                  data-test={'password-input'}
                  type="password"
                  placeholder={t('common:password')}
                  error={!!form.formState.errors.password || !!error}
                  {...field}
                />
              </FormControl>

              {/* <FormMessage /> */}

              <Button
                asChild
                type={'button'}
                size={'sm'}
                variant={'link'}
                className={'text-xs text-[#B7B8C2]'}
              >
                <Link href={'/auth/password-reset'}>
                  <Trans i18nKey={'auth:passwordForgottenQuestion'} />
                </Link>
              </Button>
            </FormItem>
          )}
        />

        <If condition={error}>
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className={'w-4 text-[#E48787]'} />
            <AlertDescription
              className="font-inter text-[12px] font-normal leading-[18px] text-[#E48787]"
              data-test={'auth-error-message'}
            >
              <Trans
                i18nKey={`auth:errors.${errorCode}`}
                defaults={'<DefaultError />'}
                components={{ DefaultError }}
              />
            </AlertDescription>
          </div>
        </If>

        <AuthButton
          data-test="auth-submit-button"
          loading={loading}
          disabled={loading}
          loadingText="auth:signingIn"
        >
          <Trans i18nKey={'auth:signIn'} />
        </AuthButton>
      </form>
    </Form>
  );
}
